<template>
  <el-card class="box-card full-screen operating-payment-add">
    <el-page-header @back="$router.go(-1)" :title="$t('Back')">
      <template slot="content">
        <span style="font-size: 14px">
          {{ $t("operatingAccount") }} / {{ $t("operatingPaymentAdd") }}
        </span>
      </template>
    </el-page-header>

    <div class="op-body">
      <div class="op-balance-bar">
        <span class="op-balance-label">{{ $t("commissionBalances") }}</span>
        <span class="op-balance-total"> FCFA {{ fmt(balancesTotal) }} </span>
        <el-button
          type="text"
          size="mini"
          icon="el-icon-refresh"
          :loading="loadingBalances"
          @click="loadBalances"
        >
          {{ $t("refresh") }}
        </el-button>
      </div>

      <el-divider content-position="left">{{
        $t("beneficiaryInfo")
      }}</el-divider>

      <el-form :inline="true" size="small" @submit.native.prevent>
        <el-form-item :label="$t('phone')">
          <phone-input v-model="phone" @phone-input="onPhoneInput" />
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            size="small"
            :loading="searching"
            @click="searchCustomer"
          >
            <i class="el-icon-search"></i> {{ $t("query") }}
          </el-button>
        </el-form-item>
      </el-form>

      <div
        v-if="customer"
        class="op-customer"
        :class="{ 'is-invalid': !customerUsable }"
      >
        <div class="op-customer-name">
          {{ customer.name }} {{ customer.surname }}
          <el-tag v-if="!customerUsable" size="mini" type="danger">
            {{ $t("accountUnavailable") }}
          </el-tag>
        </div>
        <div class="op-customer-meta">
          <span
            >{{ $t("customerCode") }} : {{ customer.customerCode || "—" }}</span
          >
          <span>{{ $t("phone") }} : {{ customer.phoneNo || "—" }}</span>
          <span>{{ $t("account") }} : {{ customer.cardNo || "—" }}</span>
        </div>
      </div>

      <template v-if="customer && customerUsable">
        <el-divider content-position="left">{{ $t("paymentInfo") }}</el-divider>

        <el-form label-position="top" size="small">
          <el-row :gutter="20">
            <el-col :md="8" :lg="6">
              <el-form-item :label="$t('amount')" required>
                <el-input v-model="form.amount" @input="onAmountInput">
                  <template slot="prepend">FCFA</template>
                </el-input>
              </el-form-item>
            </el-col>

            <el-col :md="8" :lg="6">
              <el-form-item :label="$t('expenseType')" required>
                <el-select
                  v-model="form.expenseType"
                  filterable
                  :placeholder="$t('select')"
                  style="width: 100%"
                >
                  <el-option
                    v-for="t in expenseTypes"
                    :key="t.id"
                    :label="$t(t.name)"
                    :value="t.name"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :md="12">
              <el-form-item :label="$t('reason')" required>
                <el-input type="textarea" :rows="2" v-model="form.reason" />
              </el-form-item>
            </el-col>
            <el-col :md="12">
              <el-form-item :label="$t('remark')" required>
                <el-input type="textarea" :rows="2" v-model="form.remark" />
              </el-form-item>
            </el-col>
          </el-row>
          <div class="op-hint">{{ $t("reasonOrRemarkRequired") }}</div>
        </el-form>

        <el-divider content-position="left">{{
          $t("paymentSources")
        }}</el-divider>

        <div class="op-sources">
          <div v-for="(row, i) in sources" :key="i" class="op-source-row">
            <span class="op-source-index">{{ i + 1 }}</span>

            <el-select
              v-model="row.orgInfoId"
              size="small"
              filterable
              :placeholder="$t('selectOrg')"
              style="width: 320px"
              @change="onSourceOrgChange(row)"
            >
              <el-option
                v-for="b in optionsFor(row)"
                :key="b.orgInfoId"
                :label="b.orgName + ' — FCFA ' + fmt(b.accountAmount)"
                :value="b.orgInfoId"
                :disabled="num(b.accountAmount) <= 0"
              />
            </el-select>

            <el-input
              v-model="row.amount"
              size="small"
              style="width: 200px"
              @input="(v) => onSourceAmountInput(row, v)"
            >
              <template slot="prepend">FCFA</template>
            </el-input>

            <span v-if="row.orgInfoId" class="op-source-avail">
              {{ $t("available") }} : FCFA {{ fmt(balanceOf(row.orgInfoId)) }}
            </span>
            <span v-if="sourceExceeds(row)" class="op-error">
              {{ $t("300008:not sufficient funds") }}
            </span>

            <el-button
              type="text"
              icon="el-icon-delete"
              class="op-source-remove"
              @click="removeSource(i)"
            />
          </div>

          <el-button
            size="small"
            icon="el-icon-plus"
            :disabled="remaining <= 0 || sources.length >= balances.length"
            @click="addSource"
          >
            {{ $t("addSource") }}
          </el-button>
        </div>

        <div class="op-summary" :class="isBalanced ? 'ok' : 'ko'">
          <span
            >{{ $t("amount") }} : <b>FCFA {{ fmt(amountNumber) }}</b></span
          >
          <span
            >{{ $t("sourcesTotal") }} :
            <b>FCFA {{ fmt(sourcesTotal) }}</b></span
          >
          <span v-if="remaining > 0">
            {{ $t("remainingToAllocate") }} : <b>FCFA {{ fmt(remaining) }}</b>
          </span>
          <span v-if="remaining < 0">
            {{ $t("overAllocated") }} : <b>FCFA {{ fmt(-remaining) }}</b>
          </span>
          <span v-if="isBalanced">✅</span>
        </div>

        <div class="bottom-button">
          <el-button @click="resetForm">{{ $t("reset") }}</el-button>
          <el-button
            type="primary"
            :loading="submitting"
            :disabled="!canSubmit"
            @click="submit"
          >
            {{ $t("submit") }}
          </el-button>
        </div>
      </template>
    </div>
  </el-card>
</template>

<script>
import operatingPayment from "@/api/operatingPayment";
import expense from "@/api/expense";
import PhoneInput from "@/components/PhoneInput";

export default {
  name: "operatingPaymentAdd",
  components: { PhoneInput },
  data() {
    return {
      balances: [],
      balancesTotal: 0,
      loadingBalances: false,

      phone: "",
      searching: false,
      customer: null,

      expenseTypes: [],
      form: { amount: "", expenseType: "", reason: "", remark: "" },
      sources: [],
      submitting: false,
    };
  },
  computed: {
    customerUsable() {
      const c = this.customer;
      return !!c && c.status == 0 && !!c.cardNo && c.cardStatus == 0;
    },
    amountNumber() {
      return this.num(this.form.amount);
    },
    sourcesTotal() {
      return this.sources.reduce((s, r) => s + this.num(r.amount), 0);
    },
    remaining() {
      return this.round(this.amountNumber - this.sourcesTotal);
    },
    isBalanced() {
      return this.amountNumber > 0 && this.remaining === 0;
    },
    sourcesValid() {
      return (
        this.sources.length > 0 &&
        this.sources.every(
          (r) =>
            r.orgInfoId && this.num(r.amount) > 0 && !this.sourceExceeds(r),
        )
      );
    },
    canSubmit() {
      return (
        this.customerUsable &&
        this.isBalanced &&
        this.sourcesValid &&
        !!this.form.expenseType &&
        (!!this.form.reason.trim() || !!this.form.remark.trim())
      );
    },
  },
  methods: {
    num(v) {
      return parseFloat(String(v == null ? "" : v).replace(/\s/g, "")) || 0;
    },
    round(n) {
      return Math.round(n * 100) / 100;
    },
    fmt(n) {
      return Number(n || 0).formatMoney(0);
    },
    spaced(raw) {
      return raw ? raw.replace(/\B(?=(\d{3})+(?!\d))/g, " ") : "";
    },

    loadBalances() {
      this.loadingBalances = true;
      operatingPayment.commissionBalances({
        param: {},
        success: (res) => {
          this.loadingBalances = false;
          if (res.code === 200 && res.data) {
            this.balances = res.data.list || [];
            this.balancesTotal = res.data.total || 0;
          }
        },
        failing: () => {
          this.loadingBalances = false;
        },
      });
    },
    loadExpenseTypes() {
      expense.getAllExpenseType({
        param: {},
        success: (res) => {
          if (res.code === 200) this.expenseTypes = res.data || [];
        },
      });
    },

    onPhoneInput(val) {
      this.phone = val;
    },
    searchCustomer() {
      const phone = String(this.phone || "").trim();
      if (!phone) {
        this.$message.warning(this.$t("requiredFields"));
        return;
      }
      this.searching = true;
      this.customer = null;
      operatingPayment.findCustomer({
        param: { phone },
        success: (res) => {
          this.searching = false;
          if (res.code === 200 && res.data) {
            this.customer = res.data;
            if (!this.customerUsable)
              this.$message.warning(this.$t("accountUnavailable"));
          } else {
            this.$message.warning(this.$t("customerNotFound"));
          }
        },
        failing: () => {
          this.searching = false;
          this.$message.error(this.$t("networkError"));
        },
      });
    },

    onAmountInput(val) {
      this.form.amount = this.spaced(String(val || "").replace(/\D/g, ""));
    },

    balanceOf(orgInfoId) {
      const b = this.balances.find((x) => x.orgInfoId === orgInfoId);
      return b ? this.num(b.accountAmount) : 0;
    },
    optionsFor(row) {
      const taken = this.sources
        .filter((r) => r !== row && r.orgInfoId)
        .map((r) => r.orgInfoId);
      return this.balances.filter((b) => !taken.includes(b.orgInfoId));
    },
    addSource() {
      this.sources.push({ orgInfoId: null, amount: "" });
    },
    removeSource(i) {
      this.sources.splice(i, 1);
    },
    onSourceOrgChange(row) {
      const others = this.sources
        .filter((r) => r !== row)
        .reduce((s, r) => s + this.num(r.amount), 0);
      const need = Math.max(this.amountNumber - others, 0);
      const fill = Math.min(this.balanceOf(row.orgInfoId), need);
      row.amount = this.spaced(String(Math.floor(fill)));
    },
    onSourceAmountInput(row, val) {
      row.amount = this.spaced(String(val || "").replace(/\D/g, ""));
    },
    sourceExceeds(row) {
      return (
        row.orgInfoId && this.num(row.amount) > this.balanceOf(row.orgInfoId)
      );
    },

    submit() {
      if (!this.canSubmit) return;

      this.$confirm(
        this.$t("operatingPaymentConfirm", {
          amount: this.fmt(this.amountNumber),
          name: this.customer.name + " " + this.customer.surname,
        }),
        this.$t("confirm"),
        {
          confirmButtonText: this.$t("confirm"),
          cancelButtonText: this.$t("cancel"),
          type: "warning",
        },
      )
        .then(() => {
          this.submitting = true;
          const sourcesJson = JSON.stringify(
            this.sources.map((r) => ({
              orgInfoId: r.orgInfoId,
              amount: this.num(r.amount),
            })),
          );

          operatingPayment.add({
            param: {
              customerId: this.customer.customerId,
              amount: this.amountNumber,
              expenseType: this.form.expenseType,
              reason: this.form.reason.trim(),
              remark: this.form.remark.trim(),
              sourcesJson,
            },
            success: (res) => {
              this.submitting = false;
              if (res.code === 200) {
                this.$message.success(this.$t("operatingPaymentCreated"));
                this.resetForm();
                this.loadBalances();
              } else {
                this.$message.warning(this.errorMessage(res.msg));
              }
            },
            failing: () => {
              this.submitting = false;
              this.$message.error(this.$t("networkError"));
            },
          });
        })
        .catch(() => {});
    },

    errorMessage(msg) {
      if (!msg) return this.$t("operationFailure");
      const i = msg.indexOf("insufficient:");
      if (i >= 0)
        return this.$t("insufficientSource", {
          org: msg.substring(i + "insufficient:".length),
        });
      return this.$t(msg);
    },

    resetForm() {
      this.phone = "";
      this.customer = null;
      this.form = { amount: "", expenseType: "", reason: "", remark: "" };
      this.sources = [];
    },
  },
  watch: {
    customer(val) {
      if (val && this.sources.length === 0) this.addSource();
    },
  },
  mounted() {
    this.loadBalances();
    this.loadExpenseTypes();
  },
};
</script>

<style lang="less" scoped>
.operating-payment-add {
  overflow-y: auto;
}
.op-body {
  padding: 10px 4px 20px;
}
.op-balance-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  background: #f0f5ff;
  border-left: 3px solid #1164c4;
  border-radius: 6px;
  margin-bottom: 8px;
}
.op-balance-label {
  font-size: 13px;
  color: #606266;
}
.op-balance-total {
  font-size: 18px;
  font-weight: 700;
  color: #1164c4;
}
.op-customer {
  padding: 12px 16px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #fafafa;
  &.is-invalid {
    border-color: #f56c6c;
    background: #fef0f0;
  }
}
.op-customer-name {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
}
.op-customer-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  font-size: 13px;
  color: #606266;
}
.op-hint {
  font-size: 12px;
  color: #909399;
  margin-top: -8px;
}
.op-sources {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}
.op-source-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.op-source-index {
  width: 22px;
  height: 22px;
  line-height: 22px;
  text-align: center;
  border-radius: 50%;
  background: #1164c4;
  color: #fff;
  font-size: 12px;
}
.op-source-avail {
  font-size: 12px;
  color: #909399;
}
.op-source-remove {
  color: #f56c6c;
}
.op-error {
  font-size: 12px;
  color: #f56c6c;
  font-weight: 600;
}
.op-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin: 18px 0 8px;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 13px;
  &.ok {
    background: #f0f9eb;
    color: #67c23a;
  }
  &.ko {
    background: #fdf6ec;
    color: #e6a23c;
  }
}
</style>
